import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import {
  logCorrelationId,
  StructuredLogger,
} from './structured-logger.service';
import {
  LogTestContext,
  LogTestScenario,
  structuredLogsEnabled,
  StructuredEvent,
} from './structured-log-policy';

export interface LogTestResult {
  events: StructuredEvent[];
  flush: 'completed' | 'timed_out' | 'failed' | 'disabled';
}

/** Exercises only the real logging pipeline. Never injects client data or API services. */
@Injectable({ providedIn: 'root' })
export class LoggingTestService {
  private readonly logger = inject(StructuredLogger);

  enabled(): boolean {
    return (
      structuredLogsEnabled() &&
      Sentry.getClient()?.getOptions().enableLogs === true
    );
  }

  async run(
    scenario: LogTestScenario,
    testRunId: string,
  ): Promise<LogTestResult> {
    if (!this.enabled()) return { events: [], flush: 'disabled' };
    const context: LogTestContext = {
      source: 'logging_test',
      test_run_id: testRunId,
      test_scenario: scenario,
    };
    const events: StructuredEvent[] = [];
    switch (scenario) {
      case 'action_assignment_unverified':
        this.logger
          .action('care_plan.action.update', context)
          .begin()
          .fail({ status: 503 }, 'verification', true, undefined, {
            completed_count: 1,
            operation_count: 2,
          });
        events.push('fa.action.outcome_unknown');
        break;
      case 'api_failed':
        this.logger.emit('api.request.failed', {
          ...context,
          feature: 'api',
          stage: 'request',
          method: 'GET',
          route_template: '/accounts',
          request_id: logCorrelationId(),
          http_status: 504,
          reason_code: 'timeout',
          outcome: 'failed',
          duration_ms: 1000,
        });
        events.push('api.request.failed');
        break;
      case 'contract_mismatch':
        this.logger.emit('api.response.contract_mismatch', {
          ...context,
          feature: 'api',
          stage: 'response',
          contract_name: 'accounts.inventory',
          reason_code: 'contract_mismatch',
          outcome: 'failed',
        });
        events.push('api.response.contract_mismatch');
        break;
      case 'action_recovered': {
        const sequence = this.logger.action('care_plan.action.update', context);
        sequence.begin().fail({ status: 409 }, 'save', true);
        sequence.begin().succeed('save');
        events.push('fa.action.blocked', 'fa.action.recovered');
        break;
      }
      case 'outcome_partial':
        this.logger
          .action('care_plan.outcome.reassign', context)
          .begin()
          .fail({ status: 503 }, 'link', true, undefined, {
            completed_count: 1,
            operation_count: 2,
          });
        events.push('fa.action.outcome_unknown');
        break;
      case 'option_qualification_failed':
        this.logger
          .action('care_plan.option.qualify', context)
          .begin()
          .fail({ status: 503 }, 'qualification', false);
        events.push('fa.action.failed');
        break;
      case 'option_override_blocked':
        this.logger
          .action('care_plan.option.override_qualification', context)
          .begin()
          .fail({ status: 403 }, 'save', true);
        events.push('fa.action.blocked');
        break;
      case 'goal_partial':
        this.logger
          .action('care_plan.goal.save_batch', context)
          .begin()
          .fail({ status: 409 }, 'save_goals', true, undefined, {
            completed_count: 1,
            operation_count: 3,
          });
        events.push('fa.action.blocked');
        break;
      case 'goal_recovered': {
        const sequence = this.logger.action(
          'care_plan.goal.save_batch',
          context,
        );
        sequence.begin().fail({ status: 409 }, 'save_goals', true);
        sequence.begin().succeed('save_goals');
        events.push('fa.action.blocked', 'fa.action.recovered');
        break;
      }
      case 'review_retry_failed':
        this.logger
          .action('ai_review.activity.retry', context)
          .begin()
          .fail({ status: 403 }, 'acceptance', true);
        events.push('fa.action.blocked');
        break;
      case 'send_unknown':
        this.logger
          .action('financial_snapshot.send', context)
          .begin()
          .fail({ status: 504 }, 'acceptance', true);
        events.push('fa.action.outcome_unknown');
        break;
      case 'metadata_recovered': {
        const operation = {
          ...context,
          feature: 'client_metadata',
          stage: 'refresh',
          trigger: 'user',
          interaction_id: logCorrelationId(),
        };
        this.logger.emit('feature.operation.failed', {
          ...operation,
          outcome: 'failed',
          reason_code: 'network',
          attempt_count: 1,
        });
        this.logger.emit('feature.operation.recovered', {
          ...operation,
          outcome: 'recovered',
          attempt_count: 2,
        });
        events.push('feature.operation.failed', 'feature.operation.recovered');
        break;
      }
      case 'credit_render_failed':
        this.logger.emit('feature.operation.failed', {
          ...context,
          feature: 'credit_overview',
          stage: 'render_page',
          trigger: 'user',
          outcome: 'failed',
          reason_code: 'unknown',
        });
        events.push('feature.operation.failed');
        break;

      case 'token_refresh_failed':
        this.logger.emit('auth.token_refresh.failed', {
          ...context,
          feature: 'authentication',
          stage: 'token_refresh',
          outcome: 'failed',
          reason_code: 'refresh_failed',
        });
        events.push('auth.token_refresh.failed');
        break;
      case 'forced_logout':
        this.logger.emit('auth.session.forced_logout', {
          ...context,
          feature: 'authentication',
          stage: 'sign_out',
          outcome: 'requested',
          reason_code: 'permission_denied',
        });
        events.push('auth.session.forced_logout');
        break;
      case 'connector_timeout':
        this.logger.emit('connector.connection.failed', {
          ...context,
          feature: 'connectors',
          stage: 'connection',
          outcome: 'failed',
          reason_code: 'timeout',
        });
        events.push('connector.connection.failed');
        break;
      case 'connector_recovered': {
        const connection = {
          ...context,
          feature: 'connectors',
          stage: 'connection',
          interaction_id: logCorrelationId(),
        };
        this.logger.emit('connector.connection.failed', {
          ...connection,
          outcome: 'failed',
          reason_code: 'popup_blocked',
          attempt_count: 1,
        });
        this.logger.emit('connector.connection.recovered', {
          ...connection,
          outcome: 'recovered',
          attempt_count: 2,
        });
        events.push(
          'connector.connection.failed',
          'connector.connection.recovered',
        );
        break;
      }
      case 'permission_denied':
        this.logger
          .action('ai_review.activity.confirm', context)
          .begin()
          .fail({ status: 403 }, 'acceptance', true);
        events.push('fa.action.blocked');
        break;
      case 'response_lost':
        this.logger
          .action('care_plan.case.create', context)
          .begin()
          .fail({ status: 0 }, 'create_case', true);
        events.push('fa.action.outcome_unknown');
        break;
      case 'submission_failed':
        this.logger
          .action('care_plan.case.create', context)
          .begin()
          .fail(new Error('Synthetic pre-submit failure'), 'submission', false);
        events.push('fa.action.failed');
        break;
      case 'retry_recovered': {
        const sequence = this.logger.action('care_plan.case.create', context);
        sequence.begin().fail({ status: 409 }, 'create_case', true);
        sequence.begin().succeed('create_case');
        events.push('fa.action.blocked', 'fa.action.recovered');
        break;
      }
      case 'navi_recovered': {
        const run = {
          client_guid: '00000000-0000-4000-8000-000000000003',
          conversation_id: 'synthetic-conversation-1',
          run_id: 'synthetic-run-1',
          ...context,
          feature: 'copilot',
          interaction_id: logCorrelationId(),
          stage: 'execution',
          attempt_count: 1,
        };
        this.logger.emit('copilot.run.reconnecting', {
          ...run,
          outcome: 'reconnecting',
        });
        this.logger.emit('copilot.run.recovered', {
          ...run,
          outcome: 'recovered',
        });
        events.push('copilot.run.reconnecting', 'copilot.run.recovered');
        break;
      }
      case 'flags_failed':
        this.logger.emit('feature_flags.initialization_failed', {
          ...context,
          feature: 'feature_flags',
          stage: 'initialization',
          reason_code: 'dependency_unavailable',
        });
        events.push('feature_flags.initialization_failed');
        break;
    }
    try {
      return {
        events,
        flush: (await Sentry.flush(5000)) ? 'completed' : 'timed_out',
      };
    } catch {
      return { events, flush: 'failed' };
    }
  }
}
